import { useCallback, useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  EmbeddedCheckoutProvider,
  EmbeddedCheckout,
} from "@stripe/react-stripe-js";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";

interface EmbeddedCheckoutFormProps {
  priceIds: string[];
  mode: string;
  tags: string[];
  salespersonId?: string;
  commissionRate?: number | null;
  utm?: Record<string, string>;
  promoCode?: string;
}

const EmbeddedCheckoutForm = ({
  priceIds,
  mode,
  tags,
  salespersonId,
  commissionRate,
  utm,
  promoCode,
}: EmbeddedCheckoutFormProps) => {
  const [stripePromise, setStripePromise] = useState<ReturnType<typeof loadStripe> | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Load Stripe with publishable key from edge function
  useEffect(() => {
    const loadKey = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("get-stripe-key");
        if (error || !data?.publishableKey) {
          setError("Failed to load payment configuration");
          return;
        }
        setStripePromise(loadStripe(data.publishableKey));
      } catch {
        setError("Failed to load payment configuration");
      }
    };
    loadKey();
  }, []);

  const fetchClientSecret = useCallback(async () => {
    const { data, error } = await supabase.functions.invoke("create-checkout", {
      body: {
        priceIds,
        mode,
        tags,
        embedded: true,
        ...(salespersonId ? { salespersonId } : {}),
        ...(commissionRate != null ? { commissionRate } : {}),
        ...(utm && Object.keys(utm).length > 0 ? { utm } : {}),
        ...(promoCode ? { promoCode } : {}),
      },
    });

    // Surface clinical block (HTTP 422) as a clear inline message
    const ctx = (error as { context?: Response } | null)?.context;
    if (ctx && ctx.status === 422) {
      try {
        const payload = await ctx.clone().json();
        const msg = payload?.message || "This plan isn't available based on your health assessment.";
        setError(msg);
        throw new Error(msg);
      } catch {
        setError("This plan isn't available based on your health assessment.");
        throw error;
      }
    }

    if (error || !data?.clientSecret) {
      throw new Error(error?.message || "Failed to create checkout session");
    }

    return data.clientSecret;
  }, [priceIds, mode, tags, salespersonId, commissionRate, utm, promoCode]);

  if (error) {
    return (
      <div className="flex items-center justify-center p-8 text-destructive">
        <p>{error}</p>
      </div>
    );
  }

  if (!stripePromise) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div id="checkout" className="w-full">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ fetchClientSecret }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
};

export default EmbeddedCheckoutForm;
